<script setup lang="ts">
import { reactive, ref } from 'vue';
import BaseForm from '@/shared/components/BaseForm.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import { ComRegistrationForm } from '../models/ComRegistrationForm';
import type { RuleObject } from 'ant-design-vue/es/form';
import { message } from 'ant-design-vue';
import { comApi } from '@/http/api';
import commissioningApiClient from '@/http/commissioningApiClient';
import { validateStrongPassword } from '@/utils/validators';

const formRef = ref();
const nameInput = ref<{ focus?: () => void } | null>(null);
const registrationForm = reactive<ComRegistrationForm>(new ComRegistrationForm());
const isSaving = ref(false);

const emit = defineEmits<{
  (event: 'success', email: string, password: string): void;
}>();

const validationRules: Record<string, RuleObject[]> = {
  name: [{ required: true, message: 'Name is required', trigger: ['blur', 'change'] }],
  companyName: [
    { required: true, message: 'Company name is required', trigger: ['blur', 'change'] },
  ],
  companyElectricalLicenseNumber: [
    {
      required: true,
      message: 'Company electrical license number is required',
      trigger: ['blur', 'change'],
    },
  ],
  allumeTrainingCertificationNumber: [
    {
      required: true,
      message: 'Allume training certification number is required',
      trigger: ['blur', 'change'],
    },
    {
      validator: (_, value) => {
        if (value && !/([0-9a-fA-F]{4}-){3}([0-9a-fA-F]{4})/.test(value)) {
          return Promise.reject('Invalid Allume training certification number');
        }
        return Promise.resolve();
      },
      trigger: ['blur', 'change'],
    },
  ],
  email: [
    { required: true, message: 'Email is required', trigger: ['blur', 'change'] },
    { type: 'email', message: 'Please enter a valid email address', trigger: ['blur', 'change'] },
  ],
  password: [
    { validator: validateStrongPassword, trigger: ['blur', 'change'] },
  ],
  confirmPassword: [
    {
      validator: (_, value) => {
        if (value !== registrationForm.password) {
          return Promise.reject('Passwords do not match');
        }
        return Promise.resolve();
      },
      trigger: ['blur', 'change'],
    },
  ],
};

const handleSubmit = async () => {
  if (isSaving.value) return;

  try {
    await formRef.value?.validate();
    isSaving.value = true;
    await commissioningApiClient.post(
      comApi.commRegister,
      registrationForm.serialize(),
    );
    message.success(
      'Registration successful. Please check your email and click the verification link.',
      10,
    );
    emit('success', registrationForm.email, registrationForm.password);
  } catch (error: unknown) {
    console.error('Error registering user:', error);
    message.error((error as Error).message);
  } finally {
    isSaving.value = false;
  }
};

const resetForm = () => {
  formRef.value?.resetFields?.();
  formRef.value?.clearValidate?.();
  Object.assign(registrationForm, new ComRegistrationForm());
};

const focusFirstInput = () => {
  nameInput.value?.focus?.();
};

defineExpose({
  focusFirstInput,
  resetForm,
});
</script>

<template>
  <BaseForm
    ref="formRef"
    class="register-form"
    :model="registrationForm"
    :rules="validationRules"
    @finish="handleSubmit"
    id="com-register-form"
  >
    <a-form-item name="name" :rules="validationRules.name">
      <template #label>
        <span class="form-label">Name</span>
      </template>
      <a-input
        ref="nameInput"
        v-model:value="registrationForm.name"
        size="large"
        data-tn="com-register-name-input"
      />
    </a-form-item>
    <a-form-item name="companyName" :rules="validationRules.companyName">
      <template #label>
        <span class="form-label">Company name</span>
      </template>
      <a-input
        v-model:value="registrationForm.companyName"
        size="large"
        data-tn="com-register-company-name-input"
      />
    </a-form-item>
    <a-form-item
      name="companyElectricalLicenseNumber"
      :rules="validationRules.companyElectricalLicenseNumber"
    >
      <template #label>
        <span class="form-label">Company electrical license number</span>
      </template>
      <a-input
        v-model:value="registrationForm.companyElectricalLicenseNumber"
        size="large"
        data-tn="com-register-company-electrical-license-number-input"
      />
    </a-form-item>
    <a-form-item
      name="allumeTrainingCertificationNumber"
      :rules="validationRules.allumeTrainingCertificationNumber"
    >
      <template #label>
        <span class="form-label">Allume training certification number</span>
      </template>
      <a-input
        v-model:value="registrationForm.allumeTrainingCertificationNumber"
        size="large"
        data-tn="com-register-allume-training-certification-number-input"
      />
    </a-form-item>
    <a-form-item name="email" :rules="validationRules.email">
      <template #label>
        <span class="form-label">Email</span>
      </template>
      <a-input v-model:value="registrationForm.email" size="large" data-tn="com-register-email-input" />
    </a-form-item>
    <a-form-item name="password" :rules="validationRules.password">
      <template #label>
        <span class="form-label">Password</span>
      </template>
      <a-input-password
        v-model:value="registrationForm.password"
        size="large"
        data-tn="com-register-password-input"
      />
    </a-form-item>
    <a-form-item name="confirmPassword" :rules="validationRules.confirmPassword">
      <template #label>
        <span class="form-label">Confirm password</span>
      </template>
      <a-input-password
        v-model:value="registrationForm.confirmPassword"
        size="large"
        data-tn="com-register-confirm-password-input"
      />
    </a-form-item>

    <div class="submit-button-container">
      <ButtonPrimary
        :loading="isSaving"
        html-type="submit"
        form="com-register-form"
        class="submit-button"
        block
        data-tn="com-register-submit-button"
        >Register</ButtonPrimary
      >
    </div>
  </BaseForm>
</template>

<style lang="less" scoped>
@import '../../../assets/style/variables.less';

.form-label {
  color: @color-primary;
  font-weight: @font-weight-medium;
}

:deep(.ant-input) {
  border-radius: @border-radius-medium !important;
  border-color: @color-primary !important;
}

:deep(.ant-input-affix-wrapper) {
  border-radius: @border-radius-medium !important;
  border-color: @color-primary !important;
}

:deep(.ant-form-item-explain-error) {
  margin-bottom: 10px;
}

.submit-button-container {
  position: sticky;
  bottom: -33px;
  left: 0;
  margin: 40px -40px 0 -40px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 -6px 12px -8px rgba(0, 0, 0, 0.25);
  z-index: 2;
}
</style>